import { MUTED } from '../Landing/ui';

const MONTHS = [
  'jan',
  'feb',
  'mar',
  'apr',
  'may',
  'jun',
  'jul',
  'aug',
  'sep',
  'oct',
  'nov',
  'dec',
];
const LABELS = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'];

// Parse "Nov-Feb", "November to March", "May – September" into a set of
// month indices (wraparound included). Unparseable ranges simply don't
// highlight — the strip stays decorative, the text below carries the facts.
function monthsInRange(monthRange) {
  if (!monthRange) return [];
  const found = [];
  const lower = monthRange.toLowerCase();
  MONTHS.forEach((month, i) => {
    const pos = lower.indexOf(month);
    if (pos !== -1) found.push({ pos, i });
  });
  if (found.length === 0) return [];
  found.sort((a, b) => a.pos - b.pos);
  if (found.length === 1) return [found[0].i];
  const start = found[0].i;
  const end = found[found.length - 1].i;
  const months = [];
  for (let i = start; ; i = (i + 1) % 12) {
    months.push(i);
    if (i === end || months.length >= 12) break;
  }
  return months;
}

// Many destinations describe the whole year (high + shoulder + low), so
// "mentioned in a range" alone would paint all twelve cells the same.
// Classify each range by its description instead — the strip's job is to
// show the best months at a glance.
const HIGH = 3;
const SHOULDER = 2;
const LOW = 1;
function seasonLevel(seasonDescription) {
  const description = (seasonDescription || '').toLowerCase();
  if (/(high|peak|best)[\s-]*(season|time)/.test(description)) return HIGH;
  if (/shoulder|(spring|autumn|fall) season/.test(description))
    return SHOULDER;
  if (
    /(low|off)[\s-]*season|rainy season|monsoon|hurricane season/.test(
      description,
    )
  )
    return LOW;
  return 0;
}

const CELL_CLASSES = {
  [HIGH]:
    'tw-bg-primary-600/[0.38] tw-text-primary-900 dark:tw-bg-primary-300/[0.38] dark:tw-text-primary-100',
  [SHOULDER]:
    'tw-bg-primary-600/[0.14] tw-text-primary-800 dark:tw-bg-primary-300/[0.14] dark:tw-text-primary-200',
  [LOW]: 'tw-bg-[#F1ECDF] tw-text-[#8A958F] dark:tw-bg-[#1A2A24] dark:tw-text-[#5F6F69]',
};
const LEGEND_LABELS = {
  [HIGH]: 'High season',
  [SHOULDER]: 'Shoulder',
  [LOW]: 'Low season',
};
const LEGEND_SWATCHES = {
  [HIGH]: 'tw-bg-primary-600/[0.38] dark:tw-bg-primary-300/[0.38]',
  [SHOULDER]: 'tw-bg-primary-600/[0.14] dark:tw-bg-primary-300/[0.14]',
  [LOW]: 'tw-bg-[#F1ECDF] dark:tw-bg-[#1A2A24]',
};

// "When to go" as a stamped calendar strip: twelve month cells tinted by
// season quality (best months pop, low season stays quiet), season notes
// below with dashed hairlines.
export default function TravelCalendar({ travelTimes }) {
  if (!travelTimes || travelTimes.length === 0) return null;

  // A month claimed by two ranges keeps the stronger season.
  const monthLevels = new Array(12).fill(0);
  let classified = false;
  travelTimes.forEach(({ monthRange, seasonDescription }) => {
    const level = seasonLevel(seasonDescription);
    if (level > 0) classified = true;
    monthsInRange(monthRange).forEach(i => {
      monthLevels[i] = Math.max(monthLevels[i], level);
    });
  });
  // Descriptions without season keywords: fall back to the binary
  // "mentioned = in season" tint so the strip still says something.
  if (!classified)
    travelTimes.forEach(({ monthRange }) => {
      monthsInRange(monthRange).forEach(i => {
        monthLevels[i] = SHOULDER;
      });
    });

  const levelsPresent = [HIGH, SHOULDER, LOW].filter(level =>
    monthLevels.includes(level),
  );

  return (
    <div>
      <div
        aria-hidden="true"
        className="tw-grid tw-grid-cols-12 tw-gap-1 sm:tw-gap-1.5"
      >
        {LABELS.map((label, i) => (
          <span
            key={MONTHS[i]}
            className={`tw-rounded tw-py-1.5 tw-text-center tw-font-mono tw-text-[10px] tw-font-semibold ${
              CELL_CLASSES[monthLevels[i]] || CELL_CLASSES[LOW]
            }`}
          >
            {label}
          </span>
        ))}
      </div>

      {classified && levelsPresent.length > 1 && (
        <div
          aria-hidden="true"
          className="tw-mt-3 tw-flex tw-flex-wrap tw-items-center tw-gap-x-5 tw-gap-y-1"
        >
          {levelsPresent.map(level => (
            <span
              key={level}
              className="tw-inline-flex tw-items-center tw-gap-1.5 tw-font-mono tw-text-[9px] tw-font-semibold tw-uppercase tw-tracking-[0.2em] tw-text-[#8A958F]"
            >
              <span
                className={`tw-h-2.5 tw-w-2.5 tw-rounded-sm ${LEGEND_SWATCHES[level]}`}
              />
              {LEGEND_LABELS[level]}
            </span>
          ))}
        </div>
      )}

      <ul className="tw-m-0 tw-mt-6 tw-list-none tw-p-0">
        {travelTimes.map(({ monthRange, seasonDescription }, i) => (
          <li
            key={monthRange}
            className={`tw-flex tw-flex-col tw-gap-1.5 tw-py-4 sm:tw-flex-row sm:tw-items-baseline sm:tw-gap-5 ${
              i > 0
                ? 'tw-border-0 tw-border-t tw-border-dashed tw-border-[#C9C0AA] dark:tw-border-[#3A4D44]'
                : ''
            }`}
          >
            <span className="tw-shrink-0 tw-font-mono tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-[0.18em] tw-text-primary-700 dark:tw-text-primary-300 sm:tw-w-32">
              {monthRange}
            </span>
            <span className={`${MUTED} tw-text-[15px] tw-leading-relaxed`}>
              {seasonDescription}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
